import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@reach/router";
import styled from "styled-components";
import { NavigationProps } from "../utils/navigationProps";
import { usePageTitle } from "../hooks/usePageTitle";
import { ThemeProps } from "../styles/theme";
import { Content, Message, Page, PageTitle, PrimaryButton } from "../train/trainUi";
import { api } from "../api/api";
import { TtsVoice, TtsVoiceDto, TtsVoicesDto } from "../common/api";

const VOICE_ID = /^[A-Za-z0-9]{8,64}$/;

const LANGUAGES: { voice: TtsVoice; title: string; note: string; sample: string }[] = [
    {
        voice: 'native',
        title: 'Russian',
        note: 'Translations, hints and the announcements.',
        sample: 'Доброе утро! Карточка три из десяти. Дети громко смеются во дворе.',
    },
    {
        voice: 'greek',
        title: 'Greek',
        note: 'Everything written in Greek.',
        sample: 'Καλημέρα! Τα παιδιά γελάνε δυνατά στην αυλή του σχολείου.',
    },
];

const BackLink = styled(Link)<ThemeProps>`
    font-size: 14px;
    color: ${p => p.theme.secondaryText};
    margin-bottom: 12px;
`;

const Section = styled.section<ThemeProps>`
    border: 1px solid ${p => p.theme.secondaryBorder};
    background-color: ${p => p.theme.secondaryBackground};
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2<ThemeProps>`
    font-size: 17px;
    margin: 0 0 4px;
    color: ${p => p.theme.primaryText};
`;

const Note = styled.div<ThemeProps>`
    font-size: 13px;
    color: ${p => p.theme.secondaryText};
    margin-bottom: 12px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    flex-wrap: wrap;
`;

const VoiceId = styled.code<ThemeProps>`
    font-size: 13px;
    color: ${p => p.theme.secondaryText};
    flex: 1 1 200px;
    overflow-wrap: anywhere;
`;

const Input = styled.input<ThemeProps>`
    flex: 1 1 160px;
    min-width: 0;
    padding: 6px 8px;
    font-size: 14px;
    font-family: inherit;
    border-radius: 4px;
    color: ${p => p.theme.primaryText};
    background-color: ${p => p.theme.primaryBackground};
    border: 1px solid ${p => p.theme.secondaryBorder};
`;

const IdInput = styled(Input)`
    font-family: monospace;
`;

const IconButton = styled.button<ThemeProps>`
    width: 36px;
    height: 32px;
    flex: none;
    font-size: 14px;
    border-radius: 4px;
    cursor: pointer;
    color: ${p => p.theme.neutralButtonForeground};
    background-color: ${p => p.theme.neutralButtonBackground};
    border: 1px solid ${p => p.theme.neutralButtonBorder};

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

const SmallButton = styled(PrimaryButton)`
    margin-top: 0;
    flex: none;
`;

const AddRow = styled(Row)<ThemeProps>`
    border-top: 1px dashed ${p => p.theme.secondaryBorder};
    margin-top: 8px;
    padding-top: 12px;
`;

const Status = styled.span<ThemeProps & { $error?: boolean }>`
    font-size: 13px;
    margin-left: 12px;
    color: ${p => p.$error === true ? p.theme.dangerColor : p.theme.secondaryText};
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
`;

type Draft = Record<TtsVoice, { id: string; name: string }>;

const EMPTY_DRAFT: Draft = { native: { id: '', name: '' }, greek: { id: '', name: '' } };

interface Playback {
    key: string;
    status: 'loading' | 'playing' | 'failed';
}

/** Plays one voice sample at a time; clips are kept for the page's lifetime. */
function usePreview() {
    const audio = useRef<HTMLAudioElement | null>(null);
    const clips = useRef(new Map<string, string>());
    const [playback, setPlayback] = useState<Playback | undefined>(undefined);

    useEffect(() => {
        const urls = clips.current;
        return () => {
            audio.current?.pause();
            urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const stop = useCallback(() => {
        audio.current?.pause();
        setPlayback(undefined);
    }, []);

    const play = useCallback(async (voice: TtsVoice, voiceId: string, text: string) => {
        const key = `${voice}|${voiceId}`;
        audio.current?.pause();
        if (audio.current === null) {
            audio.current = new Audio();
            audio.current.addEventListener('ended', () => setPlayback(undefined));
        }
        const player = audio.current;
        setPlayback({ key, status: 'loading' });

        try {
            let url = clips.current.get(key);
            if (url === undefined) {
                url = URL.createObjectURL(await api.tts({ text, voice, voiceId }));
                clips.current.set(key, url);
            }
            player.src = url;
            await player.play();
            setPlayback(current => (current?.key === key ? { key, status: 'playing' } : current));
        } catch {
            setPlayback(current => (current?.key === key ? { key, status: 'failed' } : current));
        }
    }, []);

    return { playback, play, stop };
}

export const CarVoices: FC<NavigationProps> = () => {
    usePageTitle('Car voices · Memkeep');

    const [voices, setVoices] = useState<TtsVoicesDto | undefined>(undefined);
    const [saved, setSaved] = useState<TtsVoicesDto | undefined>(undefined);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [status, setStatus] = useState<{ text: string; error?: boolean } | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const { playback, play, stop } = usePreview();

    useEffect(() => {
        api.ttsVoices()
            .then(loaded => {
                setVoices(loaded);
                setSaved(loaded);
            })
            .catch(() => setStatus({ text: 'Could not load the voices.', error: true }));
    }, []);

    const dirty = voices !== undefined && JSON.stringify(voices) !== JSON.stringify(saved);

    const update = (voice: TtsVoice, list: TtsVoiceDto[]) => {
        setVoices(current => (current !== undefined ? { ...current, [voice]: list } : current));
        setStatus(undefined);
    };

    const save = async () => {
        if (voices === undefined)
            return;
        setSaving(true);
        try {
            const result = await api.saveTtsVoices(voices);
            setVoices(result);
            setSaved(result);
            setStatus({ text: 'Saved. Car mode uses these voices from the next card.' });
        } catch (error) {
            setStatus({ text: error instanceof Error ? error.message : 'Saving failed.', error: true });
        } finally {
            setSaving(false);
        }
    };

    const playButton = (voice: TtsVoice, voiceId: string, sample: string) => {
        const key = `${voice}|${voiceId}`;
        const state = playback?.key === key ? playback.status : undefined;
        const label = state === 'loading' ? '…' : state === 'playing' ? '■' : state === 'failed' ? '!' : '▶';
        return (
            <IconButton
                title={state === 'failed' ? 'Could not play this voice' : 'Play a sample'}
                disabled={!VOICE_ID.test(voiceId)}
                onClick={() => (state === 'playing' || state === 'loading' ? stop() : void play(voice, voiceId, sample))}
            >
                {label}
            </IconButton>
        );
    };

    return (
        <Page>
            <Content>
                <BackLink to="/car">← Car mode</BackLink>
                <PageTitle>Car voices</PageTitle>

                {voices === undefined ? (
                    <Message>{status?.text ?? 'Loading…'}</Message>
                ) : (
                    <>
                        <Note>
                            ElevenLabs voices Car mode speaks with. Each card gets one voice per language,
                            picked at random from its list.
                        </Note>

                        {LANGUAGES.map(({ voice, title, note, sample }) => {
                            const list = voices[voice];
                            const next = draft[voice];
                            const nextId = next.id.trim();
                            const canAdd = VOICE_ID.test(nextId) && !list.some(entry => entry.id === nextId);

                            const add = () => {
                                const name = next.name.trim();
                                update(voice, [...list, name.length > 0 ? { id: nextId, name } : { id: nextId }]);
                                setDraft(current => ({ ...current, [voice]: { id: '', name: '' } }));
                            };

                            return (
                                <Section key={voice}>
                                    <SectionTitle>{title}</SectionTitle>
                                    <Note>{note}</Note>

                                    {list.map((entry, index) => (
                                        <Row key={entry.id}>
                                            {playButton(voice, entry.id, sample)}
                                            <Input
                                                value={entry.name ?? ''}
                                                placeholder="Name (optional)"
                                                onChange={event => update(voice, list.map((item, i) => (
                                                    i === index ? { ...item, name: event.target.value } : item
                                                )))}
                                            />
                                            <VoiceId>{entry.id}</VoiceId>
                                            <IconButton
                                                title={list.length === 1 ? 'A language needs at least one voice' : 'Remove'}
                                                disabled={list.length === 1}
                                                onClick={() => update(voice, list.filter((_, i) => i !== index))}
                                            >
                                                ✕
                                            </IconButton>
                                        </Row>
                                    ))}

                                    <AddRow>
                                        {playButton(voice, nextId, sample)}
                                        <Input
                                            value={next.name}
                                            placeholder="Name (optional)"
                                            onChange={event => setDraft(current => ({ ...current, [voice]: { ...current[voice], name: event.target.value } }))}
                                        />
                                        <IdInput
                                            value={next.id}
                                            placeholder="ElevenLabs voice id"
                                            onChange={event => setDraft(current => ({ ...current, [voice]: { ...current[voice], id: event.target.value } }))}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' && canAdd)
                                                    add();
                                            }}
                                        />
                                        <SmallButton disabled={!canAdd} onClick={add}>Add</SmallButton>
                                    </AddRow>
                                </Section>
                            );
                        })}

                        <Footer>
                            <PrimaryButton disabled={!dirty || saving} onClick={() => void save()}>
                                {saving ? 'Saving…' : 'Save'}
                            </PrimaryButton>
                            {status !== undefined && <Status $error={status.error}>{status.text}</Status>}
                            {status === undefined && dirty && <Status>Unsaved changes</Status>}
                        </Footer>
                    </>
                )}
            </Content>
        </Page>
    );
};
CarVoices.displayName = 'CarVoices';
